import React, { useCallback, useEffect, useState } from 'react';
import SuperadminLayout from '../../components/SuperadminLayout';
import LotteryCodeField from '../../components/LotteryCodeField';
import DateField from '../../components/DateField';
import {
  submitSuperadminWinningNumber,
  peekWinningNumber,
  fetchSuperadminWinningNumberSummary,
} from '../../api/winningNumbers';
import { todayISO } from '../../utils/date';
import { fmtWin4 } from '../../utils/lottery';
import './SuperadminWinningNumbersPage.css';

const fmtMoney = (n) => (n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const SuperadminWinningNumbersPage = () => {
  const [date, setDate] = useState(todayISO());
  const [lottery, setLottery] = useState('1');
  const [lotteryName, setLotteryName] = useState('NY MIDI');
  const [numbers, setNumbers] = useState('');
  const [win4, setWin4] = useState('');

  const [saving, setSaving] = useState(false);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [summary, setSummary] = useState({ rows: [], grand_total: 0, grand_tickets: 0 });

  const loadSummary = useCallback(async () => {
    try {
      setSummary(await fetchSuperadminWinningNumberSummary({ date }));
    } catch (err) {
      setSummary({ rows: [], grand_total: 0, grand_tickets: 0 });
    }
  }, [date]);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  const handleCheckSource = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setChecking(true);
    try {
      const src = await peekWinningNumber({ tirage: lottery, date });
      setNumbers(src.numbers);
      setWin4(src.win4);
      setSuccess(`Source : ${src.numbers} / ${fmtWin4(src.win4)} (tirage ${src.time}). Verifie puis Execute.`);
    } catch (err) {
      let msg = 'Resultat pas encore disponible sur la source.';
      try {
        const parsed = JSON.parse(err.message);
        if (parsed && parsed.detail) msg = parsed.detail;
      } catch (_) { /* garde le message par defaut */ }
      setError(msg);
    } finally {
      setChecking(false);
    }
  };

  const handleExecute = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!numbers || !win4) {
      setError('NUMBER et WIN4 sont requis.');
      return;
    }
    setSaving(true);
    try {
      const result = await submitSuperadminWinningNumber({ tirage: lottery, date, numbers, win4 });
      setSuccess(
        `Valeur globale enregistree — ${result.lines_recalculated} fiche(s) recalculee(s) `
        + 'sur toutes les companies. Les overrides par company de ce tirage/date ont ete effaces.'
      );
      loadSummary();
    } catch (err) {
      setError('Enregistrement impossible.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SuperadminLayout>
      <div className="sawn-header">
        <h2 className="sawn-title">Numeros gagnants (global)</h2>
      </div>

      <p className="sawn-warning">
        ⚠ Ceci ecrit la valeur <strong>globale</strong> : elle s'applique a <strong>toutes les companies</strong>
        {' '}et <strong>ecrase</strong> tout numero que des companies auraient saisi elles-memes pour ce tirage/cette date.
        Le scraper automatique ne touchera plus ce tirage/date une fois saisi ici.
      </p>

      <div className="sawn-card">
        <form onSubmit={handleExecute} className="sawn-form">
          <div className="sawn-row">
            <label className="sawn-label">Date</label>
            <DateField className="sawn-input" value={date} onChange={setDate} />
          </div>

          <div className="sawn-row">
            <label className="sawn-label">Lottery</label>
            <LotteryCodeField
              value={lottery}
              name={lotteryName}
              onChange={(code) => { setLottery(code); setLotteryName(''); }}
              onResolve={(code, nm) => { setLottery(code); setLotteryName(nm); }}
              inputClass="sawn-input sawn-input-sm"
              iconClass="sawn-icon-btn"
              nameClass="sawn-lottery-name"
              allowAll={false}
            />
          </div>

          <div className="sawn-row">
            <label className="sawn-label">NUMBER</label>
            <input
              type="text"
              className="sawn-input sawn-input-sm"
              value={numbers}
              onChange={e => setNumbers(e.target.value)}
            />
          </div>

          <div className="sawn-row">
            <label className="sawn-label">WIN4</label>
            <input
              type="text"
              className="sawn-input sawn-input-sm"
              value={win4}
              onChange={e => setWin4(e.target.value)}
            />
          </div>

          <a href="#/" className="sawn-check-source" onClick={handleCheckSource}>
            {checking ? 'Lecture source...' : 'Check Source'}
          </a>

          {error && <p className="sawn-error">{error}</p>}
          {success && <p className="sawn-success">{success}</p>}

          <button type="submit" className="sawn-execute-btn" disabled={saving}>
            {saving ? 'Enregistrement...' : 'Execute (global)'}
          </button>
        </form>
      </div>

      <div className="sawn-card sawn-summary-card">
        <h3 className="sawn-summary-title">Lots gagnants globaux — {date}</h3>
        <table className="sawn-summary-table">
          <thead>
            <tr>
              <th>Lot</th>
              <th>Tirage</th>
              <th>Numéro</th>
              <th>Win4</th>
              <th>Statut</th>
              <th>Overrides company</th>
              <th>Fiches gagnées (toutes companies)</th>
              <th>Somme totale</th>
            </tr>
          </thead>
          <tbody>
            {summary.rows.length === 0 && (
              <tr><td colSpan="8" className="sawn-summary-empty">Aucun lot gagnant pour cette date.</td></tr>
            )}
            {summary.rows.map((r) => (
              <tr key={r.tirage_code}>
                <td>{r.tirage_code}</td>
                <td>{r.tirage_name}</td>
                <td className="sawn-summary-num">{r.numbers || '—'}</td>
                <td className="sawn-summary-num">{fmtWin4(r.win4) || '—'}</td>
                <td>
                  <span className={`sawn-status sawn-status-${r.source || 'none'}`}>{r.status_label}</span>
                </td>
                <td className="sawn-summary-center">{r.company_overrides || ''}</td>
                <td className="sawn-summary-center">{r.winning_tickets}</td>
                <td className="sawn-summary-money">{fmtMoney(r.total_gain)}</td>
              </tr>
            ))}
          </tbody>
          {summary.rows.length > 0 && (
            <tfoot>
              <tr className="sawn-summary-total">
                <td colSpan="6">Total</td>
                <td className="sawn-summary-center">{summary.grand_tickets}</td>
                <td className="sawn-summary-money">{fmtMoney(summary.grand_total)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </SuperadminLayout>
  );
};

export default SuperadminWinningNumbersPage;
